/**
 * Landing page content — copy lifted verbatim from the FinminOS landing Figma
 * (developers file, "FinminOS" page). Keep copy edits here, not in components.
 */
import {
  BadgePercent,
  Banknote,
  BookOpenText,
  Briefcase,
  Building2,
  CalendarClock,
  ClipboardCheck,
  FileSearch,
  FileSpreadsheet,
  Globe2,
  Home,
  LineChart,
  ListChecks,
  MailCheck,
  MessageCircleQuestion,
  ReceiptText,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  UserCheck,
  UserPlus,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

/** ITR deadline shown in the announcement bar / countdowns. */
export const ITR_DEADLINE_ISO = '2026-10-31T10:00:00+05:30';
export const ITR_DEADLINE_LABEL = '31 October 2026 | 10:00 AM IST';

// ── Hero ──────────────────────────────────────────────────────────────────────

export const HERO_CHECKLIST = [
  'Free to start',
  'No payment until you see your estimate',
  'CA-reviewed filing',
  'ITR filed in 24 hours',
];

// ── Logo rows (plain text marquee, as in the design) ─────────────────────────

export const TRUSTED_BY_LOGOS = [
  'Amazon', 'Razorpay', '360One', 'Tata', 'Zerodha', 'Amadeus', 'Microsoft', 'Flipkart', 'Meesho',
];

export const TEAM_LOGOS = [
  'Amazon', 'Razorpay', 'ICICI', 'Zerodha', 'Zepto', 'Microsoft', 'Bank of America', "Master's Union", 'Udaan',
];

// ── Trust cards + stats band ──────────────────────────────────────────────────

export const TRUST_CARDS = [
  { title: 'Expert CAs & Lawyers', sub: 'On every filing' },
  { title: 'Government Authorised', sub: 'ERI-2 & SEBI registered' },
  { title: 'Full Data Privacy', sub: 'Never stored or shared' },
  { title: '500+ Customers', sub: 'Trust FinminOS' },
];

export const STATS = [
  { value: '500+', label: 'Customers Served' },
  { value: '95%', label: 'Customer Satisfaction' },
  { value: '₹1.8L', label: 'Average tax saved per customer' },
  { value: 'Expert-led', label: 'Every Filing Reviewed' },
  { value: 'One', label: 'Platform for Taxes & Financial Services' },
];

// ── One Platform: free tools + services ──────────────────────────────────────

export interface IconCard {
  icon: LucideIcon;
  title: string;
  desc: string;
}

export const FREE_TOOLS: IconCard[] = [
  { icon: MessageCircleQuestion, title: 'Ask Finny', desc: 'Your financial assistant for everyday tax and finance questions.' },
  { icon: FileSpreadsheet, title: 'Old vs New Calculator', desc: 'Compare both tax regimes using your financial profile.' },
  { icon: ScanSearch, title: 'Notice Analyzer', desc: 'Know what a tax notice means before taking action.' },
  { icon: Sparkles, title: 'Rewards & Referrals', desc: 'Earn benefits every time you engage with FinminOS.' },
  { icon: BookOpenText, title: 'Income Tax Act Explainer', desc: 'Understand tax laws in simple language.' },
];

export const SERVICES: { title: string; desc: string }[] = [
  { title: 'Income Tax Return', desc: 'Expert-reviewed filing with complete transparency.' },
  { title: 'Tax Planning', desc: 'Plan ahead instead of saving taxes at the last minute.' },
  { title: 'Advance Tax', desc: 'Know how much to pay before penalties arise.' },
  { title: 'GST & Accounting', desc: 'Business compliance made simple.' },
  { title: 'Notice Resolution', desc: 'Understand and respond to notices confidently.' },
  { title: 'HUF & Family Planning', desc: "Structure your family's finances more efficiently." },
];

// ── Testimonials ──────────────────────────────────────────────────────────────

export interface Testimonial {
  name: string;
  role: string;
  city: string;
  quote: string;
  stars: number;
  /** Avatar photo exported from the Figma (public/landing/avatar-*.png). */
  avatar: string;
}

export const TESTIMONIALS: Testimonial[] = [
  {
    name: 'Rahul S.', role: 'Senior Software Engineer', city: 'Bengaluru', stars: 5,
    avatar: '/landing/avatar-rahul.png',
    quote: 'I came to FinminOS to file my Income Tax Return. I stayed because I finally had one place to understand my taxes, investments, and financial decisions.',
  },
  {
    name: 'Priya M.', role: 'Founder', city: 'Mumbai', stars: 5,
    avatar: '/landing/avatar-priya.png',
    quote: "The biggest difference wasn't the filing. It was having experts who understood both my business and personal finances together.",
  },
  {
    name: 'Amit K.', role: 'NRI Professional', city: 'Delhi', stars: 5,
    avatar: '/landing/avatar-amit.png',
    quote: 'Managing Indian taxes from abroad always felt complicated. FinminOS made everything simple and transparent.',
  },
  {
    name: 'Sneha M.', role: 'Senior Engineering Manager', city: 'Mumbai', stars: 5,
    avatar: '/landing/avatar-sneha.png',
    quote: 'ESOPs from two companies, mutual funds, a home loan — I had no idea how to approach filing. FinminOS handled it all in one clean process.',
  },
  {
    name: 'Rohan V.', role: 'Senior Financial Analyst', city: 'Bengaluru', stars: 4.5,
    avatar: '/landing/avatar-rohan.png',
    quote: "I've used three different CA firms over the years. FinminOS is the first one where I actually understand what's happening with my money at every step.",
  },
  {
    name: 'Deepa K.', role: 'Founder', city: 'Delhi', stars: 5,
    avatar: '/landing/avatar-deepa.png',
    quote: 'Running a small business and managing personal finances separately was exhausting. FinminOS brought both together with one expert who knows the full picture.',
  },
];

// ── How FinminOS works ────────────────────────────────────────────────────────

export const HOW_IT_WORKS: IconCard[] = [
  { icon: UserPlus, title: 'Create Your Account', desc: 'Register securely in less than a minute.' },
  { icon: LineChart, title: 'Build Your Financial Profile', desc: 'Connect your financial information and upload any additional documents if required.' },
  { icon: MailCheck, title: 'Receive Your Financial Summary', desc: 'Understand your income, taxes, deductions, and important financial insights in one place.' },
  { icon: UserCheck, title: 'Expert Review', desc: 'A qualified expert reviews your profile and shares personalised recommendations.' },
  { icon: ListChecks, title: 'Choose Your Service', desc: 'File your ITR, plan taxes, respond to notices, or access any financial service you need.' },
  { icon: TrendingUp, title: 'Stay Ahead', desc: 'Track your progress, receive reminders, earn rewards, and manage your finances throughout the year.' },
];

// ── Your Financial Operating System: profiles ────────────────────────────────

export interface ProfileCard {
  icon: LucideIcon;
  title: string;
  chips: string[];
}

export const FIN_PROFILES: ProfileCard[] = [
  { icon: Wallet, title: 'High Income Salaries', chips: ['Multiple employers', 'Bonuses', 'Foreign income', 'Variable pay'] },
  { icon: Briefcase, title: 'Equity & Investments', chips: ['Stocks', 'Mutual Funds', 'ESOPs', 'RSUs', 'Capital Gains'] },
  { icon: Home, title: 'Property & Loans', chips: ['Buying a home', 'Selling property', 'Rental income', 'Home loans'] },
  { icon: Building2, title: 'Business & Professional', chips: ['GST', 'Business income', 'Advance tax', 'Professional fees'] },
  { icon: Globe2, title: 'Global Finances', chips: ['NRIs', 'Foreign assets', 'International income'] },
  { icon: Users, title: 'Family Wealth', chips: ['Parents', 'Children', 'HUF', 'Joint planning', 'Succession'] },
];

// ── Why FinminOS: fanned cards ────────────────────────────────────────────────

export interface WhyCard {
  icon: LucideIcon;
  title: string;
  /** Full copy shown on the expanded (center / hovered) state — from the Figma hover cards. */
  body: string;
  /** 'amber' cards are solid amber; 'light' cards are cream. */
  tone: 'amber' | 'light';
  /** Card background exported from the Figma (photo + tint baked in). */
  bg: string;
}

export const WHY_CARDS: WhyCard[] = [
  {
    icon: ClipboardCheck, title: 'CA-Validated Filing', tone: 'amber',
    bg: '/landing/why-ca-validated.png',
    body: "Every return is reviewed and filed by a verified, ICAI-registered Chartered Accountant. Expert eyes on your numbers before anything is submitted to the Income Tax portal. You're never just a ticket.",
  },
  {
    icon: FileSearch, title: 'All Documents in One Place', tone: 'light',
    bg: '/landing/why-alldocs.png',
    body: "Upload your documents once. They're organised, secured, and accessible throughout the filing process. No back and forth, no chasing emails, no lost files.",
  },
  {
    icon: BadgePercent, title: 'AI-Powered Tax Optimisation', tone: 'amber',
    bg: '/landing/why-ai.png',
    body: 'Your AIS, Form 26AS, capital gains, and all income sources — scanned and analysed in seconds. Our AI finds every deduction you qualify for, automatically. No manual digging, no missed savings.',
  },
  {
    icon: ShieldCheck, title: 'Transparency at Every Step', tone: 'light',
    bg: '/landing/why-transparency.png',
    body: 'Know exactly where your filing stands at all times — from document review to final submission. Every step is visible and communicated to you in real time. No guesswork, ever.',
  },
  {
    icon: CalendarClock, title: 'Done in 24 Hours', tone: 'amber',
    bg: '/landing/why-done24.png',
    body: 'From the moment you upload your documents, your ITR is filed and your ITR-V acknowledgement is in your hands — within 24 hours. Because your time is worth more than waiting weeks for a CA to get back to you.',
  },
];

// ── Footer ────────────────────────────────────────────────────────────────────

export const FOOTER_COLUMNS: { heading: string; links: string[] }[] = [
  { heading: 'Platform', links: ['Dashboard', 'Financial Summary', 'Document Vault', 'Financial Timeline', 'Rewards & Referrals'] },
  { heading: 'Services', links: ['Income Tax Return', 'Tax Planning', 'Advance Tax', 'GST Services', 'Notice Resolution', 'HUF Planning'] },
  { heading: 'Free Tools', links: ['Ask Finny (AI)', 'Old vs New Calculator', 'AIS Summary', 'Notice Analyser', 'Income Tax Act'] },
  { heading: 'Company', links: ['About', 'Careers', 'Partners', 'Contact', 'Privacy Policy', 'Terms of Use', 'Refund Policy', 'Delete Account'] },
];

// Icons referenced by the dashboard mockup (kept here so hero stays lean).
export const MOCKUP_ACTIONS: { icon: LucideIcon; label: string }[] = [
  { icon: ReceiptText, label: 'Tax' },
  { icon: Banknote, label: 'Invest' },
  { icon: FileSearch, label: 'Docs' },
];
